import React, {createContext, useContext, useReducer} from 'react';

const FinancialContext = createContext();

const initialState = {
  userProfile: {
    name: '',
    currentAge: 30,
    retirementAge: 60,
    lifeExpectancy: 80,
    monthlyIncome: 100000,
    monthlyExpenses: 50000,
    postRetirementMonthlyExpense: 0,
    incomeGrowthRate: 8,
    expenseGrowthRate: 6,
    emergencyFund: 0,
    insurance: {
      termInsurance: 0,
      healthInsurance: 0,
      termPremium: 0,
      healthPremium: 0
    },
    returnExpectations: {
      shortTerm: 7,
      mediumTerm: 10,
      longTerm: 12
    },
    inflationRates: {
      general: 6,
      education: 11,
      medical: 10
    },
    existingInvestments: {
      fixedDeposits: 0,
      mutualFunds: 0,
      stocks: 0,
      epfPpf: 0,
      ssyNsc: 0,
      chitFund: 0,
      gold: 0,
      realEstate: 0,
      others: 0,
      bonds: 0,
      silver: 0,
      copper: 0,
      pms: 0,
      crypto: 0,
    },
    assets: {
      bankSavings: 0,
      vehicles: 0,
    },
    liabilities: {
      homeLoan: 0,
      carLoan: 0,
      personalLoan: 0,
      creditCard: 0,
      educationLoan: 0,
      others: 0
    }
  },
  goals: [],
  retirementPlan: {
    // 0 = not yet customized by the user; calculateRetirementMetrics falls
    // back to userProfile.postRetirementMonthlyExpense in that case.
    monthlyIncomeRequired: 0,
    existingRetirementInvestments: 0,
    sipContribution: 0,
    lumpSumContribution: 0,
    whatIfPercentage: 40
  },
  riskProfile: {
    answers: [],
    score: 0,
    category: "Moderate",
    equityAllocation: 60,
    debtAllocation: 35,
    alternativesAllocation: 5
  },
  manualAssets: [],
  manualLiabilities: [],
  assetOverrides: {},
  insurancePolicies: [],
  investmentPlanning: {
    returns: {
      fixedDeposits: 6,
      mutualFunds: 12,
      stocks: 14,
      epfPpf: 8,
      ssyNsc: 7,
      chitFund: 5,
      gold: 8,
      realEstate: 8,
      bonds: 7,
      silver: 6,
      copper: 6,
      pms: 15,
      crypto: 20,
      bankSavings: 3,
      vehicles: 0,
      others: 6,
      emergencyFund: 5
    },
  },
};

function financialReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_USER_PROFILE':
      return {
        ...state,
        userProfile: {
          ...state.userProfile,
          ...action.payload,
          insurance: {
            ...state.userProfile.insurance,
            ...action.payload.insurance
          },
          existingInvestments: {
            ...state.userProfile.existingInvestments,
            ...action.payload.existingInvestments
          },
          assets: {
            ...state.userProfile.assets,
            ...action.payload.assets
          },
          liabilities: {
            ...state.userProfile.liabilities,
            ...action.payload.liabilities
          },
          returnExpectations: {
            ...state.userProfile.returnExpectations,
            ...action.payload.returnExpectations
          },
          inflationRates: {
            ...state.userProfile.inflationRates,
            ...action.payload.inflationRates
          }
        }
      };

    case 'ADD_GOAL':
      const newGoal = {
        ...action.payload,
        id: Date.now()
      };
      return {
        ...state,
        goals: [...state.goals, newGoal]
      };

    case 'UPDATE_GOAL':
      return {
        ...state,
        goals: state.goals.map(goal =>
          goal.id === action.payload.id ? {...goal, ...action.payload} : goal
        )
      };

    case 'DELETE_GOAL':
      return {
        ...state,
        goals: state.goals.filter(goal => goal.id !== action.payload)
      };

    case 'UPDATE_RETIREMENT_PLAN':
      return {
        ...state,
        retirementPlan: {
          ...state.retirementPlan,
          ...action.payload
        }
      };

    case 'UPDATE_INVESTMENT_RETURNS':
      return {
        ...state,
        investmentPlanning: {
          ...state.investmentPlanning,
          returns: {
            ...state.investmentPlanning.returns,
            ...action.payload
          }
        }
      };

    case 'ADD_MANUAL_ASSET':
      return {
        ...state,
        manualAssets: [
          ...state.manualAssets,
          {...action.payload, id: `asset_${Date.now()}`}
        ]
      };

    case 'UPDATE_MANUAL_ASSETS':
      return {
        ...state,
        manualAssets: action.payload
      };

    case 'DELETE_MANUAL_ASSET':
      return {
        ...state,
        manualAssets: state.manualAssets.filter(a => a.id !== action.payload)
      };

    case 'ADD_MANUAL_LIABILITY':
      return {
        ...state,
        manualLiabilities: [
          ...state.manualLiabilities,
          {...action.payload, id: `liab_${Date.now()}`}
        ]
      };

    case 'DELETE_MANUAL_LIABILITY':
      return {
        ...state,
        manualLiabilities: state.manualLiabilities.filter(l => l.id !== action.payload)
      };

    case 'UPDATE_ASSET_OVERRIDE':
      return {
        ...state,
        assetOverrides: {
          ...state.assetOverrides,
          [action.payload.key]: {
            ...state.assetOverrides[action.payload.key],
            ...action.payload.overrides
          }
        }
      };

    case 'UPDATE_RISK_PROFILE':
      return {
        ...state,
        riskProfile: action.payload
      };

    case 'HYDRATE_STATE':
      // Merge cloud-loaded data (profile/goals/retirement) into local state,
      // e.g. after logging in on a new device. Only overwrites the slices
      // that were actually returned.
      return {
        ...state,
        ...(action.payload.userProfile ? { userProfile: { ...state.userProfile, ...action.payload.userProfile } } : {}),
        ...(action.payload.goals ? { goals: action.payload.goals } : {}),
        ...(action.payload.retirementPlan ? { retirementPlan: { ...state.retirementPlan, ...action.payload.retirementPlan } } : {})
      };

    default:
      return state;
  }
}

export function FinancialProvider({children}) {
  const [state, dispatch] = useReducer(financialReducer, initialState);

  return (
    <FinancialContext.Provider value={{state, dispatch}}>
      {children}
    </FinancialContext.Provider>
  );
}

export function useFinancial() {
  const context = useContext(FinancialContext);
  if (!context) throw new Error('useFinancial must be used within a FinancialProvider');
  return context;
}